import { useState, useEffect, useRef } from 'react';
import { appApi, getTokenApp, setTokenApp, sairDoEspelho, temEspelhoAberto, ErroApp, type ClienteApp, type ConviteApp } from './api';
import { EntradaApp } from './EntradaApp';
import { Aplicativo } from './Aplicativo';
import { FaixaEspelho } from './FaixaEspelho';
import { useAuth } from '../../lib/auth';
import { VisaoLoja } from '../atender/VisaoLoja';
import { garantirPersistencia } from './armazenamento';
import { useVersaoNoAr } from '../../lib/useVersaoNoAr';

// Raiz do aplicativo do cliente (`/app`).
//
// Rota PUBLICA — nao passa pelo `ExigirAuth` nem pelo `<Layout>` do ERP, do mesmo
// jeito que o catalogo. Quem manda aqui e' a credencial de cliente
// (`fiodecobre.app.token`), que e' de outra natureza e nao se cruza com a da
// equipe: as duas podem conviver no mesmo navegador.
//
// Onda 1 (RDS chamados 1.27): cadastro/entrada e chat com a loja, por texto ou
// audio gravado. "Somente isso por enquanto" — decisao do Ruan.

export default function AppCliente() {
  // Quem entrou pela porta da LOJA (credencial da equipe, a mesma do sistema). O
  // `AuthProvider` envolve o app inteiro, entao esta pergunta ja tem resposta aqui
  // sem uma chamada nova.
  //
  // ⚠️ **A equipe vem ANTES do cliente de proposito.** No celular do Ruan convivem as
  // duas credenciais — ele tem conta de cliente pra testar o app —, e sem esta ordem
  // o app abriria na conversa dele com a propria loja em vez da lista de todo mundo.
  // Sair da visao da loja (o botao do cabecalho) apaga so a credencial de equipe e
  // devolve o app do cliente, que e' como ele volta a testar do outro lado.
  const { usuario: daLoja, loading: conferindoEquipe } = useAuth();
  const [cliente, setCliente] = useState<ClienteApp | null>(null);
  const [conferindo, setConferindo] = useState(true);
  const [convite, setConvite] = useState<(ConviteApp & { token: string }) | null>(null);
  const [semConexao, setSemConexao] = useState(false);
  /** Contador do "tentar de novo": mudar ele re-dispara a conferencia da sessao. */
  const [tentativa, setTentativa] = useState(0);

  // Codigo velho na tela: recarrega, mas so num MOMENTO SEGURO.
  //
  // ⚠️ Aqui existe gente digitando mensagem, entao a recarga imediata do quiosque nao
  // serve — ela perderia a frase pela metade. A regra: release nova detectada com o app
  // ESCONDIDO recarrega na hora (ninguem esta olhando); detectada com o app aberto,
  // espera a proxima vez que ele volta do segundo plano. Num celular de balcao, que e'
  // pego e largado o dia inteiro, isso acontece em minutos.
  //
  // Por que isto e' necessario justamente aqui: a `BALCAO` e' a unica conta isenta do
  // corte das 05h ([DA-414]), entao e' o unico aparelho que nada nunca obriga a
  // recarregar. Medido em 16/08/2026 — passou dias com o JavaScript de antes da
  // [DA-545] e escondia o campo de digitar num celular que o servidor deixava responder.
  const precisaAtualizar = useRef(false);
  useVersaoNoAr(() => {
    if (document.visibilityState === 'hidden') { window.location.reload(); return; }
    precisaAtualizar.current = true;
  });
  useEffect(() => {
    const aoVoltar = (): void => {
      if (document.visibilityState === 'visible' && precisaAtualizar.current) window.location.reload();
    };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, []);

  // Durabilidade da credencial no aparelho — uma vez, na abertura. Ver
  // `armazenamento.ts` pro que isto cobre (despejo por espaco) e o que NAO cobre
  // (o corte de 7 dias do ITP, que so o atalho na tela de inicio resolve).
  useEffect(() => { void garantirPersistencia(); }, []);

  // Link de 1o acesso. Duas grafias, de proposito:
  //   `/app/<token>`  — a atual (token no caminho, endereco curto pro WhatsApp)
  //   `/app?c=<token>` — a de ate 13/08/2026, aceita enquanto houver link vivo
  // O caminho vem primeiro: e' o formato que a loja manda hoje.
  //
  // ⚠️ O token some da barra de endereco assim que e' lido (`replaceState`). Ele e'
  // credencial de uso unico: deixa-lo na URL o poe no historico do navegador, no
  // print que a pessoa manda pedindo ajuda e no `Referer` de qualquer link que ela
  // clicar depois. Convite invalido/vencido nao vira erro — a tela simplesmente
  // cai no cadastro normal, que continua funcionando.
  useEffect(() => {
    const doCaminho = window.location.pathname.match(/^\/app\/([A-Za-z0-9_-]+)\/?$/);
    const token = doCaminho ? doCaminho[1] : new URLSearchParams(window.location.search).get('c');
    if (!token) return;
    window.history.replaceState({}, '', '/app');
    appApi.lerConvite(token)
      .then((c) => setConvite({ ...c, token }))
      .catch(() => { /* link gasto ou vencido: segue o cadastro comum */ });
  }, []);

  // Token guardado vale? A resposta e' do servidor, nao do localStorage: a
  // `token_versao` pode ter mudado (senha trocada, acesso revogado) e o token
  // local continuaria com cara de bom.
  //
  // ⚠️ **SO 401 apaga a credencial.** Ate 14/08/2026 este `catch` era vazio e
  // jogava fora o token em QUALQUER falha — inclusive falta de rede. Um app na
  // tela de inicio abre com frequencia antes do celular reencontrar o sinal, e o
  // resultado era irreversivel: a credencial de 30 dias morria por causa de dois
  // segundos sem internet, e o cliente caia na tela de senha sem entender por que.
  // Quem esta sem rede precisa de "tente de novo", nunca de "faca login outra vez".
  useEffect(() => {
    // ⚠️ Credencial de espelho descartada (venceu, ou uma rota devolveu 401 e o
    // `request` a limpou) NAO pode virar "faca login de novo": a minha credencial
    // esta guardada ao lado, e voltar pra ela e' o desfecho certo ([DA-634]).
    // Aqui, e nao em cada tela, porque este e' o unico ponto por onde a ausencia
    // de token passa.
    if (!getTokenApp() && temEspelhoAberto()) sairDoEspelho();
    if (!getTokenApp()) { setConferindo(false); return; }
    let vivo = true;
    setConferindo(true);
    appApi.eu()
      .then((s) => {
        if (!vivo) return;
        // Token renovado a cada abertura — ver o comentario da rota `/me`.
        setTokenApp(s.token);
        setCliente(s.cliente);
        setSemConexao(false);
      })
      .catch((err) => {
        if (!vivo) return;
        if (err instanceof ErroApp && err.status === 401) {
          // Sessao realmente encerrada (senha trocada, acesso revogado). O
          // `request` ja limpou; explicito aqui porque e' a unica saida legitima.
          //
          // ⚠️ **Espelho vencido nao pode derrubar a MINHA sessao** ([DA-634]): o
          // token de espelho dura 2h, entao ele e' o candidato natural a vencer
          // com o app aberto. Aqui a credencial guardada volta pro lugar e a
          // proxima conferencia roda com ela — sem isto, esquecer um espelho
          // aberto custaria digitar a senha de novo.
          if (temEspelhoAberto() && sairDoEspelho()) { setTentativa((t) => t + 1); return; }
          setTokenApp(null);
          return;
        }
        setSemConexao(true);
      })
      .finally(() => { if (vivo) setConferindo(false); });
    return () => { vivo = false; };
  }, [tentativa]);

  if (conferindo || conferindoEquipe) {
    return (
      <div className="app-cliente-root" style={{ minHeight: '100dvh', backgroundColor: '#f7f7f7' }} />
    );
  }

  // Mesma porta, mesmo icone instalado: o que muda e' quem entrou.
  //
  // ⚠️ **Aqui houve uma trava por LISTA DE LOGIN, e ela durou vinte minutos** — vale
  // o registro, porque a tentacao de repor e' real. Vendo o AppCel abrir a visao da
  // loja no celular do balcao (logado como `BALCAO`), eu barrei o quiosque por nome,
  // com um campo `atende_no_app` derivado de `LOGINS_QUIOSQUE`. Estava errado por
  // dois motivos:
  //
  //  1. **O uso do aparelho nao e' o que a doc dizia.** As vendedoras atendem por ele
  //     — ouvir audio no pe da orelha, mandar foto — e' o celular compartilhado do
  //     balcao, nao so o suporte do QR do Pix.
  //  2. **A pergunta certa nao e' "que login e' esse", e' "este APARELHO pode
  //     atender"** — e quem responde isso ja e' o device-trust, por certificado, em
  //     toda chamada. O confinamento a `/pix/ativa` nunca foi uma regra sobre a conta
  //     BALCAO: e' so o que sobra quando nao ha certificado.
  //
  // Entao a decisao volta pro servidor, onde ela sempre esteve: quem nao pode atender
  // toma 403 nas rotas do atendimento, e a `VisaoLoja` DIZ isso em vez de tentar de
  // novo pra sempre. Trava no front por lista de nome envelhece sozinha e mente
  // quando o mundo muda — foi o que aconteceu aqui, no mesmo dia.
  if (daLoja) return <VisaoLoja />;

  // Tem credencial, mas nao deu pra conferir agora. A tela NAO oferece login: o
  // problema nao e' de identidade, e mandar a pessoa digitar a senha aqui seria
  // culpa-la por uma falha de rede.
  if (semConexao) {
    return (
      <div className="app-cliente-root" style={{ minHeight: '100dvh', backgroundColor: '#f7f7f7', color: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 320 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Sem conexao com a loja</div>
          <p style={{ marginTop: 8, fontSize: 14, color: '#57534e', lineHeight: 1.55 }}>
            Voce continua conectado — foi so a internet que falhou agora.
          </p>
          <button
            type="button"
            onClick={() => setTentativa((t) => t + 1)}
            style={{ marginTop: 18, height: 48, padding: '0 26px', borderRadius: 10, backgroundColor: '#b45309', color: '#fff', fontSize: 15.5, fontWeight: 700 }}
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (!cliente) return <EntradaApp aoEntrar={setCliente} convite={convite} />;
  // `aoAtualizar` existe pra Configuracoes -> Minha empresa: o servidor devolve o
  // cliente ja atualizado e a tela inteira passa a ver a marca nova sem F5.
  //
  // ⚠️ Ate 18/08/2026 esta linha entregava direto o `ChatApp`, e era por isso que o
  // app "abria no chat". Quem manda na navegacao agora e' o `Aplicativo` ([DA-575]),
  // que abre na HOME — o chat virou uma das quatro areas, nao mais a raiz.
  //
  // ⚠️ A faixa do espelho mora AQUI, acima de todas as areas ([DA-634]): o
  // `Aplicativo` devolve o chat por um caminho proprio, e uma faixa desenhada la
  // dentro sumiria justamente na tela em que confundir as identidades custa mais
  // caro. Ela e' `fixed` — quem abre espaco e' o CSS (`body:has(.faixa-espelho)`).
  return (
    <>
      {cliente.espelho && <FaixaEspelho nome={cliente.nome} />}
      <Aplicativo cliente={cliente} aoSair={() => setCliente(null)} aoAtualizar={setCliente} />
    </>
  );
}

